import React, { useState, useEffect } from 'react';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Shuffle,
  Percent,
  RefreshCw,
  Save,
  TestTube,
  Users,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const TradeOutcomeControl = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [lastUpdateInfo, setLastUpdateInfo] = useState(null);
  const [settings, setSettings] = useState({
    trading_mode: 'random',
    win_percentage: 50,
    notes: ''
  });
  const [bulkSettings, setBulkSettings] = useState({
    trading_mode: 'random',
    win_percentage: 50,
    notes: ''
  });
  const [testResults, setTestResults] = useState(null);

  const BACKEND_URL = '';

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/trade-control/bulk-settings`,
        { headers }
      );

      const data = await response.json();
      if (data.success) {
        setUsers(data.settings || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUserSettings = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/trade-control/user/${userId}/settings`,
        { headers }
      );

      const data = await response.json();
      if (data.success) {
        setSettings({
          trading_mode: data.settings.trading_mode,
          win_percentage: data.settings.win_percentage || 50,
          notes: data.settings.notes || ''
        });
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  const updateUserSettings = async () => {
    if (!selectedUser) return;

    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/trade-control/user/${selectedUser.user_id}/settings`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(settings)
        }
      );

      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: `Trading mode updated for ${selectedUser.username}`
        });
        
        // Show success banner
        setLastUpdateInfo({
          type: 'individual',
          username: selectedUser.username,
          mode: settings.trading_mode,
          percentage: settings.win_percentage
        });
        setShowSuccessBanner(true);
        setTimeout(() => setShowSuccessBanner(false), 10000);
        
        fetchAllUsers();
        setSelectedUser(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive'
      });
    }
  };

  const bulkUpdateSettings = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/trade-control/bulk-update`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(bulkSettings)
        }
      );

      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: `Updated ${data.modified_count} users`
        });
        
        // Show success banner
        setLastUpdateInfo({
          type: 'bulk',
          count: data.modified_count,
          mode: bulkSettings.trading_mode,
          percentage: bulkSettings.win_percentage
        });
        setShowSuccessBanner(true);
        setTimeout(() => setShowSuccessBanner(false), 10000);
        
        fetchAllUsers();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to bulk update',
        variant: 'destructive'
      });
    }
  };

  const testOutcome = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/trade-control/test-outcome/${userId}`,
        {
          method: 'POST',
          headers
        }
      );

      const data = await response.json();
      if (data.success) {
        setTestResults(data);
        toast({
          title: 'Test Complete',
          description: `${data.test_results.wins}% win rate over 100 simulated trades`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to test outcome',
        variant: 'destructive'
      });
    }
  };

  const getModeIcon = (mode) => {
    const icons = {
      always_win: TrendingUp,
      always_lose: TrendingDown,
      random: Shuffle,
      percentage_based: Percent
    };
    return icons[mode] || Shuffle;
  };

  const getModeColor = (mode) => {
    const colors = {
      always_win: 'text-green-400 bg-green-500/20 border-green-500',
      always_lose: 'text-red-400 bg-red-500/20 border-red-500',
      random: 'text-blue-400 bg-blue-500/20 border-blue-500',
      percentage_based: 'text-yellow-400 bg-yellow-500/20 border-yellow-500'
    };
    return colors[mode] || colors.random;
  };

  const ModeBadge = ({ mode, winPercentage }) => {
    const Icon = getModeIcon(mode);
    const colorClass = getModeColor(mode);

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        <Icon className="w-3 h-3" />
        {mode.replace('_', ' ').toUpperCase()}
        {mode === 'percentage_based' && ` (${winPercentage}%)`}
      </span>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400 font-semibold">Loading Trade Control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Target className="w-8 h-8 text-cyan-400" />
            Trade Outcome Control
          </h1>
          <p className="text-cyan-400">Control individual user trade outcomes for all trading options</p>
        </div>
        <button
          onClick={fetchAllUsers}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Warning */}
      <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-orange-400 font-semibold mb-1">⚠️ Powerful Control System</h3>
            <p className="text-orange-300 text-sm">
              This system controls trade outcomes for Binary Trading, AI Arbitrage, and all future trading features. 
              Changes apply immediately to all trading platforms. Use responsibly.
            </p>
          </div>
        </div>
      </div>

      {/* Success Banner */}
      {showSuccessBanner && lastUpdateInfo && (
        <div className="bg-green-900/30 border-2 border-green-500/50 rounded-xl p-4 animate-pulse">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-green-400 font-semibold mb-1">✅ Settings Applied Successfully!</h3>
              {lastUpdateInfo.type === 'bulk' ? (
                <p className="text-green-300 text-sm">
                  <strong>{lastUpdateInfo.count} users</strong> updated to <strong>{lastUpdateInfo.mode.replace('_', ' ')}</strong> mode 
                  {lastUpdateInfo.mode === 'percentage_based' && ` with ${lastUpdateInfo.percentage}% win rate`}.
                  All future trades will use these settings.
                </p>
              ) : (
                <p className="text-green-300 text-sm">
                  User <strong>{lastUpdateInfo.username}</strong> updated to <strong>{lastUpdateInfo.mode.replace('_', ' ')}</strong> mode 
                  {lastUpdateInfo.mode === 'percentage_based' && ` with ${lastUpdateInfo.percentage}% win rate`}.
                  All future trades will use these settings.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Control */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-cyan-400" />
          Bulk Control - Apply to All Users
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Trading Mode</label>
            <select
              value={bulkSettings.trading_mode}
              onChange={(e) => setBulkSettings({ ...bulkSettings, trading_mode: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="always_win">Always Win (100%)</option>
              <option value="always_lose">Always Lose (0%)</option>
              <option value="random">Random (50/50)</option>
              <option value="percentage_based">Custom Percentage</option>
            </select>
          </div>

          {bulkSettings.trading_mode === 'percentage_based' && (
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Win Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={bulkSettings.win_percentage}
                onChange={(e) => setBulkSettings({ ...bulkSettings, win_percentage: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              />
            </div>
          )}

          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Admin Notes</label>
            <input
              type="text"
              value={bulkSettings.notes}
              onChange={(e) => setBulkSettings({ ...bulkSettings, notes: e.target.value })}
              placeholder="Reason for change"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>

        <button
          onClick={bulkUpdateSettings}
          className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold transition-all"
        >
          Apply to All Users
        </button>
      </div>

      {/* Individual User Control */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-cyan-500/30">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-cyan-400" />
            Individual User Control
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Current Mode</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Win Rate</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Notes</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cyan-500/10">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-slate-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-semibold">{user.username}</p>
                      <p className="text-xs text-cyan-400">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ModeBadge mode={user.trading_mode} winPercentage={user.win_percentage} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white font-semibold">{user.win_percentage}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-400 text-sm">{user.notes || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          loadUserSettings(user.user_id);
                        }}
                        className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
                        title="Edit Settings"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => testOutcome(user.user_id)}
                        className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 transition-all"
                        title="Test Outcome"
                      >
                        <TestTube className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">
              Edit Trade Settings: {selectedUser.username}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Trading Mode
                </label>
                <select
                  value={settings.trading_mode}
                  onChange={(e) => setSettings({ ...settings, trading_mode: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                >
                  <option value="always_win">Always Win (100%)</option>
                  <option value="always_lose">Always Lose (0%)</option>
                  <option value="random">Random (50/50)</option>
                  <option value="percentage_based">Custom Percentage</option>
                </select>
              </div>

              {settings.trading_mode === 'percentage_based' && (
                <div>
                  <label className="block text-cyan-400 text-sm font-semibold mb-2">
                    Win Percentage (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.win_percentage}
                    onChange={(e) => setSettings({ ...settings, win_percentage: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Set between 0-100% win rate
                  </p>
                </div>
              )}

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={settings.notes}
                  onChange={(e) => setSettings({ ...settings, notes: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  rows={3}
                  placeholder="Reason for setting this mode..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={updateUserSettings}
                className="flex-1 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
              >
                Save Changes
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Results Modal */}
      {testResults && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-purple-500 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TestTube className="w-6 h-6 text-purple-400" />
              Test Results: {testResults.username}
            </h3>

            <div className="space-y-3">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-cyan-400 text-sm mb-2">Current Mode</p>
                <ModeBadge mode={testResults.current_mode} winPercentage={testResults.win_percentage_setting} />
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-cyan-400 text-sm mb-2">Simulation Results (100 trades)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-green-400 text-2xl font-bold">{testResults.test_results.wins}</p>
                    <p className="text-gray-400 text-xs">Wins</p>
                  </div>
                  <div>
                    <p className="text-red-400 text-2xl font-bold">{testResults.test_results.losses}</p>
                    <p className="text-gray-400 text-xs">Losses</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-cyan-500/20">
                  <p className="text-white font-semibold">
                    Actual Win Rate: {testResults.test_results.actual_win_rate}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Test confirms outcome distribution is working correctly
              </div>
            </div>

            <button
              onClick={() => setTestResults(null)}
              className="w-full mt-6 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeOutcomeControl;
